"use client";

import { useEffect } from "react";

import { classifyClientError } from "@/lib/issues/classify";
import { issueFingerprint } from "@/lib/issues/logic";
import type { IssueSurface } from "@/lib/issues/store";
import type { Json } from "@/lib/supabase/types";

const MAX_CONSOLE_ERRORS = 20;

export type ConsoleErrorEntry = {
  message: string;
  at: string;
};

export type ClientIssueInput = {
  errorMessage: string;
  stack?: string | null;
  route?: string | null;
  severity?: "low" | "med" | "high" | null;
  surface?: IssueSurface | null;
  context?: Json;
};

let consoleErrors: ConsoleErrorEntry[] = [];
let isReporting = false;
const reportedFingerprints = new Set<string>();

export function ErrorCapture({
  enabled,
  surface,
}: {
  enabled: boolean;
  surface: IssueSurface;
}) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const restoreConsole = installConsoleCapture();
    const previousOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const errorMessage =
        error instanceof Error ? error.message : String(message || "Window error");
      const stack = error instanceof Error ? error.stack ?? null : null;
      void reportClientIssue({
        errorMessage,
        stack,
        route: readCurrentRoute(),
        surface,
        context: {
          boundary: "window.onerror",
          source: typeof source === "string" ? source : null,
          lineno: typeof lineno === "number" ? lineno : null,
          colno: typeof colno === "number" ? colno : null,
        },
      });
      if (typeof previousOnError === "function") {
        return previousOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const errorMessage = messageFromUnknown(reason);
      const stack = reason instanceof Error ? reason.stack ?? null : null;
      void reportClientIssue({
        errorMessage,
        stack,
        route: readCurrentRoute(),
        surface,
        context: { boundary: "unhandledrejection" },
      });
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      restoreConsole();
      window.onerror = previousOnError;
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [enabled, surface]);

  return null;
}

export async function reportClientIssue(input: ClientIssueInput): Promise<void> {
  try {
    if (isReporting || typeof fetch !== "function") return;
    const route = redactRouteSecrets(input.route || readCurrentRoute());
    const errorMessage = input.errorMessage.trim() || "Unknown client error";
    const stack = input.stack ?? null;
    const verdict = classifyClientError(errorMessage, stack);
    if (verdict.drop) return;

    const fingerprint = issueFingerprint(errorMessage, route);
    if (reportedFingerprints.has(fingerprint)) return;
    reportedFingerprints.add(fingerprint);

    isReporting = true;
    await fetch("/api/issues", {
      method: "POST",
      headers: { "content-type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        kind: "auto",
        severity: input.severity ?? verdict.severity,
        route,
        errorMessage,
        stack,
        fingerprint,
        appSurface: input.surface ?? null,
        context: buildContext(input.context),
      }),
    });
  } catch {
    // Swallow all capture/reporting failures to avoid recursive reporting.
  } finally {
    isReporting = false;
  }
}

export function getRecentConsoleErrors(): ConsoleErrorEntry[] {
  return consoleErrors.map((entry) => ({ ...entry }));
}

export function __recordConsoleErrorForTest(...args: unknown[]): void {
  recordConsoleError(args);
}

export function __resetErrorCaptureForTest(): void {
  consoleErrors = [];
  isReporting = false;
  reportedFingerprints.clear();
}

function installConsoleCapture(): () => void {
  const original = console.error;
  console.error = (...args: unknown[]) => {
    recordConsoleError(args);
    original(...args);
  };
  return () => {
    console.error = original;
  };
}

function recordConsoleError(args: readonly unknown[]): void {
  consoleErrors = [
    ...consoleErrors,
    {
      message: args.map(messageFromUnknown).join(" "),
      at: new Date().toISOString(),
    },
  ].slice(-MAX_CONSOLE_ERRORS);
}

function buildContext(context: Json | undefined): Json {
  const base: { [key: string]: Json | undefined } = isRecord(context)
    ? { ...context }
    : {};
  base.consoleErrors = getRecentConsoleErrors().map((entry) => entry.message);

  if (typeof window !== "undefined") {
    base.viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  if (typeof navigator !== "undefined") {
    base.userAgent = navigator.userAgent;
  }

  return base;
}

function readCurrentRoute(): string {
  if (typeof window === "undefined") return "/app";
  return redactRouteSecrets(
    `${window.location.pathname}${window.location.search}`,
  );
}

/**
 * A /quiz, /collab or /brand-collab path segment IS a bearer token (a
 * candidate's quiz, a partner brand's board, Where's Gut's brand room) — reports reach the issues table and the ops Telegram
 * chat, and a route string that carries the raw segment hands every reader a
 * working link.
 */
export function redactRouteSecrets(route: string): string {
  return route
    .replace(/^\/(quiz|collab|brand-collab)\/[^/?#]+/, "/$1/[token]")
    // The Hot Takes board preview carries the board token as ?preview=.
    .replace(/([?&]preview=)[^&#]+/g, "$1[token]");
}

function messageFromUnknown(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function isRecord(value: Json | undefined): value is { [key: string]: Json | undefined } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
